import { Ionicons } from '@expo/vector-icons';
import React, { useMemo, useState } from 'react';
import { LayoutChangeEvent, Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { TeamCrest } from '@/components/matches/TeamCrest';
import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { CardBoundary } from '@/components/ui/CardBoundary';
import { Segmented } from '@/components/ui/Segmented';
import { useThemePalette } from '@/config/theme';
import type { MatchShot, MatchXgStats, TeamRef } from '@/lib/data/provider';
import { useLiveMatchStats, useMatchXg } from '@/features/matches/hooks';
import { localizeDigits } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { localizeTeamName } from '@/lib/i18n/footballNames';
import { AveragePositions } from './AveragePositions';
import { GoalMouth, anyGoalMouth } from './GoalMouth';
import { FullPitchMarkings, PitchFootball, PitchTurf } from './pitchMarkings';
import { LiveXgTicker } from './LiveXgTicker';
import { MatchPrediction } from './MatchPrediction';
import { MomentumChart } from './MomentumChart';
import { PlayerComparePanel } from './PlayerComparePanel';
import { PredictedLineup } from './PredictedLineup';
import { RefereeCard } from './RefereeCard';
import { WhereToWatch } from './WhereToWatch';
import { XgFullStats } from './XgFullStats';
import { XgMomentum } from './XgMomentum';
import { XgPlayerRatings } from './XgPlayerRatings';

// Shot-dot size envelope (radius, px) — scaled by xG. SAFE_INSET keeps dots off
// the very edge and the goals. The pitch height itself is defined alongside the
// full-pitch geometry below (PITCH_HEIGHT).
const DOT_MIN = 7;
const DOT_MAX = 24;
const SAFE_INSET = 10; // keep dots off the very edge / goals

function twoDecimals(value: number): string {
  return (Math.round(value * 100) / 100).toFixed(2);
}

/**
 * The xG hero: a single split bar, home share = home_xg / (home_xg + away_xg),
 * with the big xG numbers flanking it. The two sides flip in RTL so each team's
 * number always sits above its half of the bar.
 */
function XgHeadline({
  stats,
  homeTeam,
  awayTeam,
}: {
  stats: MatchXgStats;
  homeTeam: TeamRef | null;
  awayTeam: TeamRef | null;
}) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { locale, arabicNumerals } = useLocale();
  const home = Math.max(stats.home_xg, 0);
  const away = Math.max(stats.away_xg, 0);
  const total = home + away;
  // Even split when neither side has registered xG, so the bar never collapses.
  const homeShare = total > 0 ? home / total : 0.5;
  const homeLeads = home >= away;

  return (
    <Card className="overflow-hidden">
      <View className="items-center gap-1 px-4 pt-3">
        <AppText variant="micro" weight="bold" className="uppercase tracking-widest text-muted">
          {t('match.xgShort')}
        </AppText>
      </View>
      <View className="flex-row items-center justify-between gap-3 px-4 py-3">
        <View className="flex-1 flex-row items-center gap-2">
          <TeamCrest uri={homeTeam?.logo_url} name={localizeTeamName(homeTeam, locale)} size="sm" />
          <AppText
            variant="title"
            weight="bold"
            tabular
            className={homeLeads ? 'text-volt-bright' : 'text-soft'}
          >
            {localizeDigits(twoDecimals(home), arabicNumerals)}
          </AppText>
        </View>
        <View className="flex-1 flex-row items-center justify-end gap-2">
          <AppText
            variant="title"
            weight="bold"
            tabular
            className={!homeLeads ? 'text-volt-bright' : 'text-soft'}
          >
            {localizeDigits(twoDecimals(away), arabicNumerals)}
          </AppText>
          <TeamCrest uri={awayTeam?.logo_url} name={localizeTeamName(awayTeam, locale)} size="sm" />
        </View>
      </View>
      {/* The split bar: home (accent) vs away (dim accent), RTL-mirrored. */}
      <View className="px-4 pb-4">
        <View
          className="h-2.5 flex-row overflow-hidden rounded-full bg-surface-2"
          style={{ direction: 'ltr' }}
        >
          <View
            style={{
              flex: Math.max(homeShare, 0.02),
              backgroundColor: palette.volt,
            }}
          />
          <View style={{ width: 2, backgroundColor: palette.deep }} />
          <View
            style={{
              flex: Math.max(1 - homeShare, 0.02),
              backgroundColor: palette.teamAway,
              opacity: 0.55,
            }}
          />
        </View>
      </View>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Shotmap — a full HORIZONTAL (landscape) pitch carrying BOTH teams, each
// attacking the OPPOSITE goal (broadcast convention): home attacks the RIGHT
// goal, away the LEFT. A shot's `x` is the distance from the goal its team
// ATTACKED in that team's own frame (~0 on the line), so each chance sits toward
// its own target goal and the two clouds spread to opposite ends — never folded
// onto one. Colour (home volt vs away coral) reinforces the side; dot SIZE ∝ xG,
// dot STYLE ∝ outcome.
// ---------------------------------------------------------------------------

const PITCH_HEIGHT = 230; // a landscape pitch (16:10-ish) reads as a real broadcast pitch

/** Distance span from the attacked goal back toward halfway, in the provider's
 *  units. `shot.x` ≈ 0 on the goal line and runs up to ~85 at the deepest, the
 *  same per-team frame the average-positions map uses, so we share the span. */
const X_SPAN = 85;

function clampNum(value: number, max: number): number {
  return Math.min(max, Math.max(0, value));
}

/**
 * Map a shot onto the full HORIZONTAL pitch. `x` is the distance from the goal
 * the shooter ATTACKED, so:
 *   frac = clamp(x, X_SPAN) / X_SPAN          (0 = on the attacked goal line)
 *   home attacks the RIGHT goal → xPct = 1 − frac   (small x → near the right)
 *   away attacks the LEFT  goal → xPct = frac        (small x → near the left)
 *   left = inset + xPct · usableW
 *   top  = inset + (clamp(y,100)/100) · usableH       (y across the width)
 */
function shotPosition(shot: MatchShot, width: number): { top: number; left: number } {
  const usableW = width - SAFE_INSET * 2;
  const usableH = PITCH_HEIGHT - SAFE_INSET * 2;
  const frac = clampNum(shot.x, X_SPAN) / X_SPAN;
  const xPct = shot.team_side === 'home' ? 1 - frac : frac;
  const left = SAFE_INSET + xPct * usableW;
  const top = SAFE_INSET + (clampNum(shot.y, 100) / 100) * usableH;
  return { top, left };
}

/** Dot radius scales with xG (a 0–1 value), eased so small chances stay visible. */
function dotSize(xg: number): number {
  const norm = Math.min(1, Math.max(0, xg));
  return DOT_MIN + Math.sqrt(norm) * (DOT_MAX - DOT_MIN);
}

/** Team tint: home = volt (cyan), away = teamAway (coral). Distinct hues plus the
 *  opposite attacking ends carry the home/away read — no opacity dimming needed,
 *  so away shots stay just as legible as home. */
function teamTint(side: 'home' | 'away', palette: ReturnType<typeof useThemePalette>) {
  return side === 'home'
    ? { color: palette.volt, bright: palette.voltBright }
    : { color: palette.teamAway, bright: palette.teamAway };
}

/**
 * One shot dot. Outcome drives the STYLE on the shared full pitch:
 *  - goal      → solid team colour, a football glyph + a soft outer ring (the hero)
 *  - on-target → solid team colour (saved but on frame)
 *  - blocked   → muted, hollow, dashed-feel (a thin low-opacity ring)
 *  - off-target→ hollow outline in the team colour
 * SIZE always ∝ xG. Selection adds a bright ring + glow and lifts the z-order.
 */
function ShotDot({
  shot,
  width,
  selected,
  onPress,
}: {
  shot: MatchShot;
  width: number;
  selected: boolean;
  onPress: () => void;
}) {
  const palette = useThemePalette();
  const size = dotSize(shot.xg);
  const { top, left } = shotPosition(shot, width);
  const tint = teamTint(shot.team_side, palette);

  // Blocked sits below off-target visually: muted + faint. Order matters because
  // a blocked shot is also flagged on-target=false, so it's tested first.
  const isBlocked = shot.is_blocked && !shot.is_goal;
  const isSolid = shot.is_on_target && !isBlocked;
  const dotOpacity = isBlocked ? 0.6 : 1;

  // A GOAL is the hero: a real football glyph sitting in a soft team-colour glow
  // ring, so it pops out of the cloud of ordinary shot dots. The football is
  // sized up a little from the raw xG radius so the panels read clearly.
  if (shot.is_goal) {
    const ball = Math.max(15, size * 1.05);
    const ringPad = Math.max(5, ball * 0.34);
    const box = ball + ringPad * 2;
    return (
      <Pressable
        accessibilityRole="button"
        onPress={onPress}
        hitSlop={8}
        style={{
          position: 'absolute',
          top: top - box / 2,
          left: left - box / 2,
          width: box,
          height: box,
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 3,
        }}
      >
        {/* Soft outer glow halo in the team colour — the goal's signature pop. */}
        <View
          style={{
            position: 'absolute',
            width: box,
            height: box,
            borderRadius: box / 2,
            backgroundColor: tint.color,
            opacity: selected ? 0.34 : 0.22,
          }}
        />
        {/* A crisp team-colour ring hugging the ball. */}
        <View
          style={{
            position: 'absolute',
            width: ball + ringPad,
            height: ball + ringPad,
            borderRadius: (ball + ringPad) / 2,
            borderWidth: selected ? 2.5 : 2,
            borderColor: selected ? palette.voltBright : tint.bright,
            ...(selected
              ? { shadowColor: tint.color, shadowOpacity: 0.9, shadowRadius: 7 }
              : null),
          }}
        />
        <PitchFootball size={ball} />
      </Pressable>
    );
  }

  // Ordinary shots: a tidy dot. On-target = solid team colour; off-target =
  // hollow outline; blocked = muted hollow. Size always ∝ xG.
  const fill = isSolid ? tint.color : 'transparent';
  const border = isBlocked ? palette.muted : tint.color;

  return (
    <Pressable
      accessibilityRole="button"
      onPress={onPress}
      hitSlop={8}
      style={{
        position: 'absolute',
        top: top - size / 2,
        left: left - size / 2,
        width: size,
        height: size,
        opacity: dotOpacity,
        // The open selection sits above the rest of the cloud.
        zIndex: selected ? 2 : 1,
      }}
    >
      <View
        style={{
          width: size,
          height: size,
          borderRadius: size / 2,
          backgroundColor: fill,
          borderWidth: selected ? 2 : 1.5,
          borderColor: selected ? palette.voltBright : border,
          alignItems: 'center',
          justifyContent: 'center',
          // The open selection glows a touch (web); native shows the ring alone.
          ...(selected
            ? { shadowColor: tint.color, shadowOpacity: 0.9, shadowRadius: 6 }
            : null),
        }}
      />
    </Pressable>
  );
}

/** Tap-to-inspect detail for one shot: player · minute · xG · situation. */
function ShotDetail({ shot }: { shot: MatchShot }) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { arabicNumerals } = useLocale();
  const tint = teamTint(shot.team_side, palette);
  return (
    <View className="flex-row items-center gap-2 border-t border-line px-4 py-2.5">
      <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: tint.color }} />
      <Ionicons
        name={shot.is_goal ? 'football' : 'ellipse-outline'}
        size={14}
        color={shot.is_goal ? palette.voltBright : palette.muted}
      />
      <AppText variant="caption" weight="bold" className="flex-1 text-ink" numberOfLines={1}>
        {shot.player_name}
      </AppText>
      <AppText variant="micro" weight="medium" tabular className="text-muted">
        {localizeDigits(String(shot.minute), arabicNumerals)}&apos;
      </AppText>
      <View className="rounded-full bg-volt-glass px-2 py-0.5">
        <AppText variant="micro" weight="bold" tabular className="text-volt-bright">
          {t('match.xgValue', { value: localizeDigits(twoDecimals(shot.xg), arabicNumerals) })}
        </AppText>
      </View>
    </View>
  );
}

/** A single legend swatch + label. `opacity` dims the whole chip (away key). */
function LegendItem({
  fill,
  border,
  label,
  opacity = 1,
}: {
  fill: string;
  border: string;
  label: string;
  opacity?: number;
}) {
  return (
    <View className="flex-row items-center gap-1.5" style={{ opacity }}>
      <View
        style={{
          width: 11,
          height: 11,
          borderRadius: 6,
          backgroundColor: fill,
          borderWidth: 1.5,
          borderColor: border,
        }}
      />
      <AppText variant="micro" className="text-muted" numberOfLines={1}>
        {label}
      </AppText>
    </View>
  );
}

/** The goal key — a mini football in a team-colour ring, mirroring the hero dot
 *  on the pitch so the "goal = football" read is consistent. */
function GoalKey({ label }: { label: string }) {
  const palette = useThemePalette();
  return (
    <View className="flex-row items-center gap-1.5">
      <View
        style={{
          width: 16,
          height: 16,
          borderRadius: 8,
          borderWidth: 1.5,
          borderColor: palette.voltBright,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <PitchFootball size={11} />
      </View>
      <AppText variant="micro" className="text-muted" numberOfLines={1}>
        {label}
      </AppText>
    </View>
  );
}

/** The "size = xG" hint, drawn as two graduated dots so the scale reads at a
 *  glance — small dot (low xG) → large dot (high xG) — next to the label. */
function SizeHint({ label }: { label: string }) {
  const palette = useThemePalette();
  return (
    <View className="flex-row items-center gap-1.5">
      <View className="flex-row items-end gap-1">
        <View style={{ width: 6, height: 6, borderRadius: 3, borderWidth: 1, borderColor: palette.muted }} />
        <View style={{ width: 13, height: 13, borderRadius: 7, borderWidth: 1.5, borderColor: palette.muted }} />
      </View>
      <AppText variant="micro" className="text-muted" numberOfLines={1}>
        {label}
      </AppText>
    </View>
  );
}

/**
 * Compact legend: the team colour key (home volt vs away coral — reinforcing the
 * sides on the shared full pitch), the outcome key (goal / on-target / blocked /
 * off-target) and the "size = xG" graduated hint.
 */
function ShotLegend({
  homeTeam,
  awayTeam,
}: {
  homeTeam: TeamRef | null;
  awayTeam: TeamRef | null;
}) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { locale } = useLocale();
  return (
    <View className="gap-2 px-4 py-2.5">
      {/* Team colour key — the primary read on a shared pitch. */}
      <View className="flex-row flex-wrap items-center gap-x-4 gap-y-1.5">
        <LegendItem
          fill={palette.volt}
          border={palette.voltBright}
          label={localizeTeamName(homeTeam, locale, true)}
        />
        <LegendItem
          fill={palette.teamAway}
          border={palette.teamAway}
          label={localizeTeamName(awayTeam, locale, true)}
        />
      </View>
      {/* Outcome key + the xG size hint. */}
      <View className="flex-row flex-wrap items-center gap-x-4 gap-y-1.5">
        <GoalKey label={t('match.goal')} />
        <LegendItem fill={palette.volt} border={palette.volt} label={t('match.onTarget')} />
        <LegendItem fill="transparent" border={palette.muted} label={t('match.blocked')} opacity={0.55} />
        <LegendItem fill="transparent" border={palette.voltLine} label={t('match.offTarget')} />
        <SizeHint label={t('match.sizeIsXg')} />
      </View>
    </View>
  );
}

function Shotmap({
  shots,
  homeTeam,
  awayTeam,
}: {
  shots: MatchShot[];
  homeTeam: TeamRef | null;
  awayTeam: TeamRef | null;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  // 'pitch' = the full horizontal shotmap (both teams); 'goal' = the front-on
  // goal-frame placement. NO home/away toggle any more — both teams share one pitch.
  const [mode, setMode] = useState<'pitch' | 'goal'>('pitch');
  const [selected, setSelected] = useState<MatchShot | null>(null);
  const [width, setWidth] = useState(0);

  const onLayout = (e: LayoutChangeEvent) => setWidth(e.nativeEvent.layout.width);

  // The Goal view ALWAYS has something to show now: it plots true goal-mouth
  // coords when the provider supplies them, and otherwise falls back to laying
  // on-target shots across the goal width by their `y`. So we offer the toggle
  // whenever ANY on-target shot exists (or any carries goal-mouth coords).
  const goalAvailable = useMemo(
    () => anyGoalMouth(shots) || shots.some((s) => s.is_on_target || s.is_goal),
    [shots],
  );
  const activeMode = goalAvailable ? mode : 'pitch';

  return (
    <Card className="overflow-hidden p-0">
      <View className="flex-row items-center justify-between bg-surface-2/50 px-4 py-2.5">
        <AppText variant="micro" weight="bold" className="uppercase tracking-wide text-soft">
          {activeMode === 'goal' ? t('match.goalPlacement') : t('match.shotmap')}
        </AppText>
        <AppText variant="micro" weight="medium" className="text-muted" numberOfLines={1}>
          {`${localizeTeamName(homeTeam, locale, true)} · ${localizeTeamName(awayTeam, locale, true)}`}
        </AppText>
      </View>
      {/* Pitch / Goal view switch (shown whenever the goal view has on-target
          shots to plot — true goal-mouth coords or the y-based fallback). */}
      {goalAvailable ? (
        <View className="px-4 pb-1 pt-2">
          <Segmented
            options={[
              { value: 'pitch', label: t('match.viewPitch') },
              { value: 'goal', label: t('match.viewGoal') },
            ]}
            value={activeMode}
            onChange={(value) => {
              setMode(value);
              setSelected(null);
            }}
          />
        </View>
      ) : null}
      {activeMode === 'goal' ? (
        <GoalMouth shots={shots} homeTeam={homeTeam} awayTeam={awayTeam} />
      ) : (
        <>
          {/* One-line plain explanation of the full-pitch convention. */}
          <AppText variant="micro" className="px-4 pb-1.5 pt-1 text-muted" numberOfLines={2}>
            {t('match.shotmapHint')}
          </AppText>
          {/* The full pitch is always read left→right (home attacks right, away
              attacks left), even in RTL, so the home/away orientation never flips. */}
          <View
            className="overflow-hidden bg-pitch"
            style={{ height: PITCH_HEIGHT, direction: 'ltr' }}
            onLayout={onLayout}
          >
            <PitchTurf orientation="horizontal" />
            {width > 0 ? (
              <FullPitchMarkings width={width} height={PITCH_HEIGHT} inset={SAFE_INSET} />
            ) : null}
            {width > 0
              ? shots.map((shot, index) => (
                  <ShotDot
                    key={`${shot.team_side}-${shot.minute}-${shot.player_name}-${index}`}
                    shot={shot}
                    width={width}
                    selected={selected === shot}
                    onPress={() => setSelected(shot)}
                  />
                ))
              : null}
            {shots.length === 0 ? (
              <View className="flex-1 items-center justify-center">
                <AppText variant="micro" className="text-pitch-line">
                  {t('match.noShots')}
                </AppText>
              </View>
            ) : null}
          </View>
          <ShotLegend homeTeam={homeTeam} awayTeam={awayTeam} />
          {selected ? <ShotDetail shot={selected} /> : null}
        </>
      )}
    </Card>
  );
}

/**
 * Advanced match-center panel (SofaScore-grade). In order: a live xG ticker
 * (only while the fixture is live and a fresh snapshot exists), the xG headline
 * bar, the cumulative xG momentum race, a tappable shotmap per team (with a
 * Pitch/Goal placement switch), player ratings (each row opening a profile
 * sheet), a referee card, and the complete grouped stats list with a phase
 * toggle. Silent-hides entirely when the fixture has no advanced data at all
 * (and no live row); otherwise EACH section hides independently when its own
 * slice (live / stats / shots / players / referee) is empty, since many matches
 * have some slices but not others.
 */
export function XgPanel({
  fixtureId,
  homeTeam,
  awayTeam,
  enabled = true,
  isLive = false,
  isFinished = false,
}: {
  fixtureId: string;
  homeTeam: TeamRef | null;
  awayTeam: TeamRef | null;
  enabled?: boolean;
  /** When the fixture is live, the panel polls a live snapshot for the ticker. */
  isLive?: boolean;
  /** Once the fixture is finished, the pre-match forecast cards are suppressed
   *  (a prediction / predicted XI / where-to-watch reads backwards post-match). */
  isFinished?: boolean;
}) {
  const { data } = useMatchXg(fixtureId, enabled);
  // Live snapshot - polled every 30s ONLY while live; null when no fresh row.
  const { data: live } = useLiveMatchStats(fixtureId, isLive);
  const stats = data?.stats ?? null;
  const shots = data?.shots ?? [];
  const players = data?.players ?? [];
  const referee = data?.referee ?? null;
  const liveRow = isLive ? (live ?? null) : null;

  // LIVE-prefer the momentum wave + per-minute xG race off the live snapshot
  // (refreshed ~every 3 min by sync-bzzoiro-live) so the two timeline charts move
  // mid-match. The static match_advanced_stats series (synced ~every 2h) is the
  // fallback when there's no live row yet — finished / upcoming matches keep using
  // it exactly as before. A live row whose series is null (early minutes / legacy
  // row) also falls through to the static series.
  const liveMomentum =
    (liveRow?.momentum?.length ?? 0) > 0 ? liveRow?.momentum ?? null : null;
  const livePerMinute =
    (liveRow?.xg_per_minute?.length ?? 0) > 0 ? liveRow?.xg_per_minute ?? null : null;
  const momentumSeries = liveMomentum ?? stats?.momentum ?? null;
  const perMinuteSeries = livePerMinute ?? stats?.xg_per_minute ?? null;

  // Pre-match slices: present (non-null) on an UPCOMING fixture even though it
  // has no xG / shots / players yet. Each renders independently.
  const prediction = stats?.prediction ?? null;
  const predictedLineup = stats?.predicted_lineup ?? null;
  const broadcasts = stats?.broadcasts ?? null;

  // Post-match guards. An upcoming fixture has a stats ROW (for the pre-match
  // slices) but no xG numbers / momentum / full stat table, so each post-match
  // section must self-hide on its own data, never on the row's mere presence.
  // - xG headline: hide when BOTH xG numbers are absent (null/undefined).
  const hasXg =
    stats != null && stats.home_xg != null && stats.away_xg != null;
  // - momentum: needs a per-minute curve (live-preferred) OR ≥2 shots to plot.
  const hasMomentum =
    (perMinuteSeries?.length ?? 0) > 0 || (stats != null && shots.length >= 2);
  // - momentum WAVE (365-style attacking pressure): needs the signed series
  //   (live-preferred so a live match with only the live wave still renders).
  const hasMomentumWave = (momentumSeries?.length ?? 0) > 0;
  // - full stats: only when the grouped table actually synced.
  const hasFullStats = stats != null && stats.stats_full != null;

  // Pre-match forecast cards read backwards once the match has been played, so
  // they're suppressed on a FINISHED fixture. Prediction may linger into a live
  // match; the predicted XI / where-to-watch are strictly pre-kickoff.
  const showPrediction = prediction != null && !isFinished;
  const showPredictedLineup = predictedLineup != null && !isFinished && !isLive;
  const showBroadcasts = !isFinished && !isLive && (broadcasts?.length ?? 0) > 0;

  // - average positions: present once the positional map synced (either side).
  const hasPositions =
    ((stats?.average_positions?.home?.length ?? 0) > 0) ||
    ((stats?.average_positions?.away?.length ?? 0) > 0);

  // Real post-match analytics present for THIS fixture (drives the graceful
  // "being prepared" state below when a played match has none yet).
  const hasAnalytics =
    hasXg ||
    hasMomentum ||
    hasMomentumWave ||
    hasFullStats ||
    hasPositions ||
    shots.length > 0 ||
    players.length > 0 ||
    referee != null;

  // Silent-hide the whole panel until SOME slice has synced - a live row alone
  // is enough to render (the ticker), since advanced data lags. A bare stats row
  // with no usable slice (neither pre-match nor post-match) hides too.
  const hasAnyContent =
    hasAnalytics ||
    showPrediction ||
    showPredictedLineup ||
    showBroadcasts ||
    liveRow != null;

  // A played (live/finished) match with no analytics yet shows a graceful
  // placeholder instead of a bare or prediction-only stub.
  const showPreparing = (isFinished || isLive) && !hasAnalytics && liveRow == null;

  if (!hasAnyContent && !showPreparing) return null;

  return (
    <View className="gap-4 px-4">
      {liveRow ? (
        <LiveXgTicker live={liveRow} homeTeam={homeTeam} awayTeam={awayTeam} />
      ) : null}

      {/* Pre-match cards (UPCOMING fixtures): forecast, predicted XI, broadcasts.
          Each is wrapped so a single malformed (provider-shaped, unvalidated)
          blob can never crash the whole match screen — the card just vanishes. */}
      {showPrediction ? (
        <CardBoundary>
          <MatchPrediction prediction={prediction} homeTeam={homeTeam} awayTeam={awayTeam} />
        </CardBoundary>
      ) : null}
      {showPredictedLineup ? (
        <CardBoundary>
          <PredictedLineup lineup={predictedLineup} homeTeam={homeTeam} awayTeam={awayTeam} />
        </CardBoundary>
      ) : null}
      {showBroadcasts && broadcasts ? (
        <CardBoundary>
          <WhereToWatch broadcasts={broadcasts} />
        </CardBoundary>
      ) : null}

      {/* Post-match analytics: each self-hides for an upcoming match. */}
      {hasXg ? <XgHeadline stats={stats} homeTeam={homeTeam} awayTeam={awayTeam} /> : null}
      {hasMomentumWave ? (
        <CardBoundary>
          <MomentumChart
            momentum={momentumSeries}
            shots={shots}
            homeTeam={homeTeam}
            awayTeam={awayTeam}
          />
        </CardBoundary>
      ) : null}
      {hasMomentum ? (
        <XgMomentum
          shots={shots}
          stats={stats}
          livePerMinute={livePerMinute}
          homeTeam={homeTeam}
          awayTeam={awayTeam}
        />
      ) : null}
      {shots.length > 0 ? (
        <Shotmap shots={shots} homeTeam={homeTeam} awayTeam={awayTeam} />
      ) : null}
      <CardBoundary>
        <AveragePositions stats={stats} homeTeam={homeTeam} awayTeam={awayTeam} />
      </CardBoundary>
      {players.length > 0 ? (
        <XgPlayerRatings players={players} homeTeam={homeTeam} awayTeam={awayTeam} />
      ) : null}
      {players.length > 0 ? (
        <CardBoundary>
          <PlayerComparePanel players={players} homeTeam={homeTeam} awayTeam={awayTeam} />
        </CardBoundary>
      ) : null}
      {hasFullStats ? <XgFullStats stats={stats} /> : null}
      {referee ? <RefereeCard referee={referee} /> : null}

      {showPreparing ? <PreparingState /> : null}
    </View>
  );
}

/** Graceful inline state for a played match whose advanced analytics haven't
 *  synced yet — replaces an empty / prediction-only stub. */
function PreparingState() {
  const palette = useThemePalette();
  const { t } = useTranslation();
  return (
    <Card className="items-center gap-2 px-6 py-8">
      <Ionicons name="analytics-outline" size={26} color={palette.muted} />
      <AppText variant="caption" className="text-center leading-5 text-soft">
        {t('match.xgPreparing')}
      </AppText>
    </Card>
  );
}
